import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { cn } from '../../lib/utils';

/**
 * A coach's identity block: photo (initials when there is none, or while it
 * loads), name and title (#758). The one component that draws a coach
 * everywhere a guest meets one: the public booking page's coach picker and
 * confirmation card, and the funnel end page's coach card (EndPageRenderer).
 *
 * `children` render under the title, inside the text column, so a caller can
 * add its own lines (the picker's availability hint, the funnel card's bio
 * and contact links) without re-laying out the block.
 */
export function coachInitials(name = '') {
    return String(name).split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0].toUpperCase()).join('') || '?';
}

const SIZES = {
    sm: { avatar: 'h-8 w-8 text-[11px]', name: 'text-sm font-medium', title: 'text-xs', gap: 'gap-2.5' },
    md: { avatar: 'h-10 w-10 text-xs', name: 'text-sm font-semibold', title: 'text-xs', gap: 'gap-3' },
    lg: { avatar: 'h-16 w-16 text-lg', name: 'text-base font-semibold', title: 'text-sm', gap: 'gap-4' },
};

export default function CoachAvatar({ photoUrl, name, title, size = 'md', className = '', children }) {
    const s = SIZES[size] || SIZES.md;
    // Rows (sm/md) truncate to one line; the large card wraps, as the funnel
    // end page always has.
    const clip = size === 'lg' ? '' : 'truncate';
    return (
        <div className={cn('flex items-start min-w-0', s.gap, className)}>
            <Avatar className={cn('flex-shrink-0', s.avatar)}>
                {/* Decorative: the name is always the adjacent text, so a
                    non-empty alt would read it twice (e.g. inside a picker row). */}
                {photoUrl ? <AvatarImage src={photoUrl} alt="" className="object-cover" /> : null}
                <AvatarFallback className="bg-stone-200 text-stone-600 font-semibold" aria-hidden="true">
                    {coachInitials(name)}
                </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 text-left">
                {name && (
                    <p className={cn('text-stone-900', clip, s.name)}
                        style={size === 'lg' ? { fontFamily: 'Manrope, sans-serif' } : undefined}>
                        {name}
                    </p>
                )}
                {title && <p className={cn('text-stone-500', clip, s.title)}>{title}</p>}
                {children}
            </div>
        </div>
    );
}
